import { usePageMeta } from '@/lib/seo';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgePoundSterling,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  MessageCircle,
  Palmtree,
  Banknote,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { LegalFooter } from '@/components/common/LegalFooter';
import { api, extractErrorMessage, setSession } from '@/lib/api';
import { trackEventThenRun } from '@/lib/analytics';
import { getStoredUtm, marketingEvent, agencyEvent } from '@/lib/marketing';
import { GoogleSignInButton, AuthDivider } from '@/components/auth/GoogleSignInButton';

const STAFF_RANGES = ['1-5', '6-10', '11-20', '20+'];

const FEATURES = [
  { icon: CalendarDays, label: 'Weekly rota builder' },
  { icon: Clock, label: 'Staff clock in/out kiosk' },
  { icon: Banknote, label: 'Automatic payslip generation' },
  { icon: MessageCircle, label: 'Instant shift notifications' },
  { icon: Palmtree, label: 'Leave request management' },
  { icon: BarChart3, label: 'Attendance & payroll dashboard' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Public self-serve signup — the only page that creates tenants without
 * the super admin. Auto-logs the new manager in on success. */
export default function SignupPage() {
  usePageMeta({
    title: 'Hospitality Staff Scheduling Software – Free Trial | RotaHQ',
    description: 'Start your free 14-day trial of RotaHQ, restaurant staff scheduling software built for UK hospitality. Create your account today. No setup fee.',
    canonicalPath: '/signup',
  });
  const [form, setForm] = useState({
    restaurant_name: '', name: '', email: '', password: '',
    phone: '', staff_count_range: '', terms_accepted: false,
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const startedRef = useRef(false);

  const set = (key, value) => {
    if (!startedRef.current) {
      startedRef.current = true;
      marketingEvent('signup_started');
    }
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.restaurant_name.trim().length < 2) e.restaurant_name = 'Restaurant name is required';
    if (!form.name.trim()) e.name = 'Your name is required';
    if (!EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email address';
    if (form.password.length < 8) e.password = 'Password must be at least 8 characters';
    if (!form.staff_count_range) e.staff_count_range = 'Pick your team size';
    if (!form.terms_accepted) e.terms_accepted = 'You must agree to the Terms & Conditions and Privacy Policy';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/public/signup', {
        restaurant_name: form.restaurant_name.trim(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim(),
        staff_count_range: form.staff_count_range,
        terms_accepted: form.terms_accepted,
        utm: getStoredUtm(), // ad-campaign attribution captured on the landing page
      });
      setSession(data.access_token, data.user);
      // Marketing conversion (dataLayer/GTM + Meta CompleteRegistration).
      marketingEvent('signup_completed', { method: 'email' });
      // Creating an account activates the 14-day trial immediately, so this
      // IS the confirmed trial activation moment (fires once per signup —
      // this code path only runs on a successful 201 from /public/signup).
      marketingEvent('trial_started', { method: 'email', trial_days: 14 });
      // NOTE: marketingEvent already forwards to GA4 (gtag) as well as the
      // GTM dataLayer and Meta pixel — no extra trackEvent call needed.
      // GA4 conversion: fire sign_up BEFORE the hard redirect below (the
      // callback/timeout guarantees the hit isn't lost mid-navigation and
      // that a blocked tracker never delays the user by more than 500ms).
      trackEventThenRun(
        'sign_up',
        { method: 'email', staff_count_range: form.staff_count_range },
        () => {
          // Full navigation (not SPA) so AuthContext re-initialises from storage.
          window.location.href = '/dashboard?welcome=1';
        },
      );
    } catch (err) {
      const msg = extractErrorMessage(err, 'Could not create your account. Please try again.');
      if (err?.response?.status === 409) {
        setErrors((e) => ({ ...e, email: msg }));
      }
      setServerError(msg);
      setSubmitting(false);
    }
  };

  const fieldError = (key) =>
    errors[key] ? (
      <p className="text-xs text-[color:var(--brand-danger)] mt-1" data-testid={`signup-error-${key}`}>{errors[key]}</p>
    ) : null;

  const trustBadges = useMemo(() => ([
    { icon: CheckCircle2, label: 'Live at 2 restaurants', tone: 'text-[color:var(--brand-success)]' },
    { icon: BadgePoundSterling, label: 'From £19/month', tone: 'text-[color:var(--brand-primary)]' },
    { icon: MessageCircle, label: 'Shift notifications included', tone: 'text-[color:var(--brand-secondary)]' },
  ]), []);

  return (
    <div className="min-h-screen bg-[color:var(--brand-bg)] text-foreground">
      {/* Top bar */}
      <header className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" aria-label="RotaHQ home" data-testid="signup-logo-home-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--brand-primary)] text-white shadow-[0_4px_12px_rgba(200,74,47,0.35)]">
            <Clock className="h-4.5 w-4.5" />
          </div>
          <span className="font-display text-lg font-semibold">RotaHQ</span>
        </Link>
        <Link to="/login" className="text-sm text-[color:var(--brand-primary)] hover:underline inline-flex items-center gap-1" data-testid="signup-signin-link-top">
          Sign in <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        {/* Hero */}
        <section className="text-center pt-6 pb-8 sm:pt-10 sm:pb-10" data-testid="signup-hero">
          <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.08] max-w-3xl mx-auto">
            Start Managing Your Restaurant Team The Smart Way
          </h1>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
            Scheduling · Attendance · Payroll · Instant Notifications — all in one place.
            <br className="hidden sm:block" />
            <span className="font-medium text-foreground"> Free 14-day trial. No credit card needed.</span>
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {trustBadges.map((b) => (
              <span key={b.label} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-[color:var(--brand-surface)] px-3 py-1.5 text-xs font-medium">
                <b.icon className={`h-3.5 w-3.5 ${b.tone}`} /> {b.label}
              </span>
            ))}
          </div>
        </section>

        <div className="grid lg:grid-cols-[1fr_460px] gap-8 items-start">
          {/* Feature list — after the form on mobile, left column on desktop */}
          <section className="order-2 lg:order-1" data-testid="signup-features">
            <h2 className="font-display text-xl font-semibold mb-4">What you get free for 14 days</h2>
            <ul className="space-y-3">
              {FEATURES.map((f) => (
                <li key={f.label} className="flex items-center gap-3 rounded-xl border border-border bg-[color:var(--brand-surface)] px-4 py-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)]">
                    <f.icon className="h-4.5 w-4.5" />
                  </span>
                  <span className="text-sm font-medium">{f.label}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Signup form */}
          <section className="order-1 lg:order-2">
            <form onSubmit={onSubmit} className="surface-card p-6 sm:p-7 space-y-4" noValidate data-testid="signup-form">
              <div>
                <h2 className="font-display text-xl font-semibold">Create your workspace</h2>
                <p className="text-xs text-muted-foreground mt-1">Takes less than a minute.</p>
              </div>

              <div>
                <GoogleSignInButton
                  label="Sign up with Google"
                  onBeforeRedirect={() => agencyEvent('google_signup_click', 'conversion', 'google_auth')}
                />
                <AuthDivider />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="su-restaurant">Restaurant Name *</Label>
                <Input id="su-restaurant" value={form.restaurant_name} onChange={(e) => set('restaurant_name', e.target.value)} placeholder="Your restaurant or café name" aria-invalid={!!errors.restaurant_name} data-testid="signup-restaurant-input" />
                {fieldError('restaurant_name')}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="su-name">Your Name *</Label>
                <Input id="su-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Enter your Name" aria-invalid={!!errors.name} data-testid="signup-name-input" />
                {fieldError('name')}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="su-email">Email Address *</Label>
                <Input id="su-email" type="email" autoComplete="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@restaurant.com" aria-invalid={!!errors.email} data-testid="signup-email-input" />
                {fieldError('email')}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="su-password">Password *</Label>
                <div className="relative">
                  <Input
                    id="su-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => set('password', e.target.value)}
                    placeholder="Min 8 characters"
                    className="pr-10"
                    aria-invalid={!!errors.password}
                    data-testid="signup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="signup-password-toggle"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {fieldError('password')}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="su-phone">Phone Number</Label>
                <Input id="su-phone" type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+447911123456" data-testid="signup-phone-input" />
                <p className="text-xs text-muted-foreground">Optional — kept on your account as a contact number</p>
              </div>

              <div className="space-y-1.5">
                <Label>How many staff do you manage? *</Label>
                <Select value={form.staff_count_range} onValueChange={(v) => set('staff_count_range', v)}>
                  <SelectTrigger aria-invalid={!!errors.staff_count_range} data-testid="signup-staff-select">
                    <SelectValue placeholder="Select team size" />
                  </SelectTrigger>
                  <SelectContent>
                    {STAFF_RANGES.map((r) => (
                      <SelectItem key={r} value={r} data-testid={`signup-staff-option-${r}`}>{r} staff</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError('staff_count_range')}
              </div>

              <div className="flex items-start gap-2.5 pt-1">
                <Checkbox
                  id="su-terms"
                  checked={form.terms_accepted}
                  onCheckedChange={(v) => set('terms_accepted', Boolean(v))}
                  className="mt-0.5"
                  data-testid="signup-terms-checkbox"
                />
                <label htmlFor="su-terms" className="text-xs leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-[color:var(--brand-primary)] underline" data-testid="signup-terms-link">Terms & Conditions</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-[color:var(--brand-primary)] underline" data-testid="signup-privacy-link">Privacy Policy</a>
                </label>
              </div>
              {fieldError('terms_accepted')}

              {serverError && (
                <div className="text-sm text-[color:var(--brand-danger)] bg-[color:var(--brand-danger-soft)] border border-[color:var(--brand-danger)]/15 rounded-lg px-3 py-2" data-testid="signup-server-error">
                  {serverError}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-base"
                data-testid="signup-submit-button"
              >
                {submitting ? 'Creating your workspace…' : (
                  <span className="inline-flex items-center gap-2">Start My Free 14-Day Trial <ArrowRight className="h-4 w-4" /></span>
                )}
              </Button>
              <p className="text-center text-xs text-muted-foreground">No credit card required. Cancel anytime.</p>
            </form>
          </section>
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-[color:var(--brand-primary)] font-medium hover:underline inline-flex items-center gap-1" data-testid="signup-signin-link">
              Sign In <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <LegalFooter />
        </footer>
      </main>
    </div>
  );
}
